import { useState, useRef, useLayoutEffect } from "react";
import { stripHtml } from "../utils/html";
import { RichText } from "../components/RichText";
import { PrivacyNotice } from "../components/PrivacyNotice";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { useIsMobile } from "../hooks/useIsMobile";
import { Parallax } from "react-scroll-parallax";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { validateContact, submitContactRequest, type ContactErrors } from "../lib/contactValidation";
import { ga4 } from "../analytics/ga4";

gsap.registerPlugin(ScrollTrigger);

const FALLBACK_CONTACT = "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=1400&h=500&fit=crop";

type FormState = "idle" | "sending" | "sent" | "error";

export function Contact() {
  const { locale } = useParams();
  useTranslation();
  const cfg = useSiteConfig();
  const ca = useCmsText("about");
  const ct = useCmsText("contact");
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  const [formState, setFormState] = useState<FormState>("idle");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState<ContactErrors>({});

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".gs-ct-reveal").forEach((el) => {
        gsap.from(el, { y: 40, opacity: 0, duration: 1, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 85%", once: true } });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (formState === "sending") return;                // guard anti doppio-submit
    const errs = validateContact(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;           // validazione condivisa
    setFormState("sending");
    try {
      await submitContactRequest({ ...form, source: "contact_page", locale: locale || "it" });
      ga4.formSubmit("contact_page", "contact_page"); // solo su invio riuscito
      setFormState("sent");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      setErrors({});
    } catch {
      setFormState("error");
      setTimeout(() => setFormState("idle"), 4000);
    }
  }

  const faqs = [
    { q: ct("faq1Q"), a: ct("faq1A") },
    { q: ct("faq2Q"), a: ct("faq2A") },
    { q: ct("faq3Q"), a: ct("faq3A") },
    { q: ct("faq4Q"), a: ct("faq4A") },
  ];

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "14px 16px",
    border: "1.5px solid rgba(28,75,59,0.15)",
    borderRadius: 12,
    fontSize: 14,
    outline: "none",
    background: "#fff",
    color: cfg.color_dark,
    fontFamily: "inherit",
    transition: "border-color 0.25s",
    boxSizing: "border-box",
  };

  return (
    <div ref={containerRef}>
      {/* ═══ HERO ═══ */}
      <section style={{ position: "relative", height: isMobile ? 280 : 400, overflow: "hidden" }}>
        <Parallax speed={isMobile ? -3 : -10} style={{ position: "absolute", inset: "-20% 0", height: "140%" }}>
          <div style={{ width: "100%", height: "100%", backgroundImage: `url(${ca.image("contactImage") ? resolveImageUrl(ca.image("contactImage")!) : FALLBACK_CONTACT})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        </Parallax>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%)" }} />
        <div style={{ position: "absolute", bottom: isMobile ? 32 : 56, left: isMobile ? 24 : 48, zIndex: 2 }}>
          <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#fff", margin: "0 0 10px" }}>
            {ct("overline")}
          </p>
          <h1 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 48, fontWeight: 400, color: "#fff", margin: 0, lineHeight: 1.1, letterSpacing: -0.5 }}>
            {ct("title")}
          </h1>
        </div>
      </section>

      {/* ═══ FORM CARD ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "48px 20px 80px" : "80px 24px 120px" }}>
        <div className="gs-ct-reveal" style={{ maxWidth: 780, margin: "0 auto", background: cfg.color_light_bg, borderRadius: 20, padding: isMobile ? "36px 24px" : "56px 64px", boxShadow: "0 4px 40px rgba(28,75,59,0.07)" }}>

          <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 26 : 36, fontWeight: 700, color: cfg.color_dark, textAlign: "center", margin: "0 0 40px", letterSpacing: -0.5 }}>
            {ct("formTitle")}
          </h2>

          {formState === "sent" ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: cfg.color_dark, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <p style={{ fontFamily: cfg.font_heading, fontSize: 22, fontWeight: 400, color: cfg.color_dark, margin: "0 0 8px" }}>{ct("sentTitle")}</p>
              <p style={{ fontSize: 14, color: "rgba(28,75,59,0.6)" }}>{ct("sentSubtitle")}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Row 1: Name + Company */}
              <div style={{ display: isMobile ? "flex" : "grid", flexDirection: "column", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: cfg.color_dark, letterSpacing: 0.2 }}>
                    {ct("name")}<span style={{ color: "rgba(28,75,59,0.4)" }}>*</span>
                  </label>
                  <input
                    type="text" required value={form.name} onChange={set("name")}
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = cfg.color_dark; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(28,75,59,0.15)"; }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: cfg.color_dark, letterSpacing: 0.2 }}>
                    {ct("company")}
                  </label>
                  <input
                    type="text" value={form.company} onChange={set("company")}
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = cfg.color_dark; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(28,75,59,0.15)"; }}
                  />
                </div>
              </div>

              {/* Row 2: Email + Phone */}
              <div style={{ display: isMobile ? "flex" : "grid", flexDirection: "column", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: cfg.color_dark, letterSpacing: 0.2 }}>
                    {ct("email")}<span style={{ color: "rgba(28,75,59,0.4)" }}>*</span>
                  </label>
                  <input
                    type="email" required value={form.email} onChange={set("email")}
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = cfg.color_dark; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(28,75,59,0.15)"; }}
                  />
                  {errors.email && <span style={{ fontSize: 12, color: "#c0392b" }}>{ct("errEmail") || "Inserisci un indirizzo email valido."}</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: cfg.color_dark, letterSpacing: 0.2 }}>
                    {ct("phone")}
                  </label>
                  <input
                    type="tel" value={form.phone} onChange={set("phone")}
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = cfg.color_dark; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(28,75,59,0.15)"; }}
                  />
                  {errors.phone && <span style={{ fontSize: 12, color: "#c0392b" }}>{ct("errPhone") || "Inserisci un numero di telefono valido."}</span>}
                </div>
              </div>

              {/* Message */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 500, color: cfg.color_dark, letterSpacing: 0.2 }}>
                  {ct("message")}<span style={{ color: "rgba(28,75,59,0.4)" }}>*</span>
                </label>
                <textarea
                  required rows={5} value={form.message} onChange={set("message")}
                  placeholder={stripHtml(ct("messagePlaceholder"))}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 120 }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = cfg.color_dark; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(28,75,59,0.15)"; }}
                />
              </div>

              {formState === "error" && (
                <p style={{ fontSize: 13, color: "#c0392b", margin: 0 }}>{ct("errorMsg")}</p>
              )}

              {/* Submit + informativa GDPR (finalità A). La presa visione avviene cliccando Invia:
                  nessuna checkbox obbligatoria (come da documento cliente). */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, paddingTop: 8 }}>
                <button
                  type="submit"
                  disabled={formState === "sending"}
                  style={{
                    background: cfg.color_dark,
                    color: "#fff", border: "none",
                    padding: "16px 48px", fontSize: 13, fontWeight: 600,
                    borderRadius: 50, cursor: formState === "sending" ? "not-allowed" : "pointer",
                    transition: "opacity .2s, background .2s",
                    letterSpacing: 0.8, textTransform: "uppercase",
                    minWidth: 220,
                    opacity: formState === "sending" ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => { if (formState !== "sending") e.currentTarget.style.opacity = "0.82"; }}
                  onMouseLeave={(e) => { if (formState !== "sending") e.currentTarget.style.opacity = "1"; }}
                >
                  {formState === "sending" ? "..." : ct("send")}
                </button>
                <PrivacyNotice variant="contact" style={{ textAlign: "center", maxWidth: 560 }} />
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ═══ INFO STRIP ═══ */}
      <section className="gs-ct-reveal" style={{ background: cfg.color_dark, padding: isMobile ? "48px 24px" : "64px 48px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: isMobile ? "flex" : "grid", flexDirection: "column", gridTemplateColumns: "1fr 1fr 1fr", gap: isMobile ? 32 : 48 }}>
          {[
            { icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", label: ca("contactEmailField") || "Email", value: cfg.contact_email || "info@granborgo.com", href: `mailto:${cfg.contact_email || "info@granborgo.com"}` },
            { icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z", label: ca("contactPhone") || "Telefono", value: cfg.contact_phone || "+4967629499360", href: `tel:${(cfg.contact_phone || "").replace(/\s/g, "")}` },
            { icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z", label: ca("contactAddress") || "Indirizzo", value: cfg.contact_address || "Granborgo GmbH\nAn der Molkerei 8\n56288 Kastellaun, Deutschland", href: undefined },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5"><path d={item.icon} /></svg>
              </div>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2.5, textTransform: "uppercase", color: "#fff", margin: 0 }}>{item.label}</p>
              {item.href ? (
                <a href={item.href} style={{ fontSize: 15, fontFamily: cfg.font_heading, color: "#fff", textDecoration: "none", lineHeight: 1.5, transition: "opacity .2s", whiteSpace: "pre-line" }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.65"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >{item.value}</a>
              ) : (
                <p style={{ fontSize: 15, fontFamily: cfg.font_heading, color: "#fff", margin: 0, lineHeight: 1.5, whiteSpace: "pre-line" }}>{item.value}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section style={{ background: cfg.color_light_bg, padding: isMobile ? "56px 24px" : "88px 48px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div className="gs-ct-reveal" style={{ marginBottom: isMobile ? 32 : 48 }}>
            <div style={{ width: 40, height: 1, background: cfg.color_muted, marginBottom: 20 }} />
            <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 24 : 32, fontWeight: 400, color: cfg.color_dark, margin: 0, letterSpacing: -0.3 }}>
              {ct("faqTitle")}
            </h2>
          </div>
          <div className="gs-ct-reveal" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: "1px solid rgba(28,75,59,0.89)" }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 0", background: "none", border: "none", cursor: "pointer", fontSize: 15, fontWeight: 500, color: cfg.color_dark, textAlign: "left" }}
                >
                  {faq.q}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cfg.color_dark} strokeWidth="2"
                    style={{ flexShrink: 0, transition: "transform .3s", transform: openFaq === i ? "rotate(180deg)" : "rotate(0)" }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <div style={{ maxHeight: openFaq === i ? 200 : 0, overflow: "hidden", transition: "max-height .3s ease" }}>
                  <RichText html={faq.a} style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.8, margin: "0 0 20px", paddingRight: 32 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
